import { createContext, useContext, useState, useCallback, useRef } from 'react';
import api from '../lib/api';

const DataCacheContext = createContext();

export function DataCacheProvider({ children }) {
  const [cache, setCache] = useState({});
  const cacheRef = useRef({});
  const pendingRequests = useRef({});
  const CACHE_DURATION = 30000; // 30 seconds cache

  // Keep cacheRef in sync with cache state
  cacheRef.current = cache;

  const getCachedData = useCallback(async (key, fetchFn, forceRefresh = false) => {
    const now = Date.now();
    const cached = cacheRef.current[key];

    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && cached && (now - cached.timestamp) < CACHE_DURATION) {
      return cached.data;
    }

    // If there's already a pending request for this key, wait for it
    if (pendingRequests.current[key]) {
      return pendingRequests.current[key];
    }

    // Create new request
    const request = fetchFn().then(data => {
      setCache(prev => ({
        ...prev,
        [key]: { data, timestamp: Date.now() }
      }));
      delete pendingRequests.current[key];
      return data;
    }).catch(error => {
      delete pendingRequests.current[key];
      throw error;
    });

    pendingRequests.current[key] = request;
    return request;
  }, []); // Empty dependency array - uses ref instead

  const invalidateCache = useCallback((keyPattern) => {
    if (!keyPattern) {
      setCache({});
      return;
    }
    setCache(prev => {
      const newCache = {};
      Object.keys(prev).forEach(key => {
        if (!key.includes(keyPattern)) {
          newCache[key] = prev[key];
        }
      });
      return newCache;
    });
  }, []);

  const updateCacheItem = useCallback((key, updateFn) => {
    setCache(prev => {
      if (!prev[key]) return prev;
      return {
        ...prev,
        [key]: {
          ...prev[key],
          data: updateFn(prev[key].data)
        }
      };
    });
  }, []);

  // Preloaded fetch functions - use configured api instance with auth
  const fetchDashboardData = useCallback(() => 
    getCachedData('dashboard', async () => {
      const response = await api.get('/dashboard');
      return response.data;
    }), [getCachedData]);

  const fetchProducts = useCallback((forceRefresh = false) => 
    getCachedData('products', async () => {
      const response = await api.get('/products');
      return response.data;
    }, forceRefresh), [getCachedData]);

  const fetchCustomers = useCallback((forceRefresh = false) => 
    getCachedData('customers', async () => {
      const response = await api.get('/customers');
      return response.data;
    }, forceRefresh), [getCachedData]);

  const fetchEmployees = useCallback((forceRefresh = false) => 
    getCachedData('employees', async () => {
      const response = await api.get('/employees');
      return response.data;
    }, forceRefresh), [getCachedData]);

  const fetchSuppliers = useCallback((forceRefresh = false) => 
    getCachedData('suppliers', async () => {
      const response = await api.get('/suppliers');
      return response.data;
    }, forceRefresh), [getCachedData]);

  const value = {
    getCachedData,
    invalidateCache,
    updateCacheItem,
    fetchDashboardData,
    fetchProducts,
    fetchCustomers,
    fetchEmployees,
    fetchSuppliers,
    CACHE_DURATION
  };

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
}
